import React,{useEffect,useRef,useState} from 'react';
import VectorEffects from './DiagramVectorEffects';

// All shader source is app-owned. The model selects five fixed effects only.
const VERTEX='attribute vec2 aPosition; void main(){gl_Position=vec4(aPosition,0.,1.);}';
const FRAGMENT=`precision mediump float;
uniform vec2 uResolution;
uniform vec4 uEffect[4];
uniform vec4 uDirection[4];
uniform vec4 uColor[4];
uniform int uCount;
void main(){
 vec2 p=vec2(gl_FragCoord.x/uResolution.x*1000.,(1.-gl_FragCoord.y/uResolution.y)*600.);
 vec4 result=vec4(0.);
 for(int i=0;i<4;i++){
  if(i>=uCount)break;
  vec2 origin=uEffect[i].xy;float r=max(1.,uEffect[i].z);float type=uEffect[i].w;
  vec2 d=uDirection[i].xy-origin;float phase=uDirection[i].z;
  vec2 axis=d/max(1.,length(d));float along=dot(p-origin,axis)/max(1.,length(d));
  float side=abs(dot(p-origin,vec2(-axis.y,axis.x)))/r;
  float alpha=0.;vec3 color=uColor[i].rgb;
  if(type<.5){alpha=exp(-dot(p-origin,p-origin)/(r*r))*.32;}
  else if(type<1.5){
   for(int k=0;k<8;k++){float t=fract(float(k)/8.+phase);vec2 q=origin+d*t;alpha+=exp(-dot(p-q,p-q)/(r*r*.006))*(1.-t)*.85;}
  }else if(type<2.5){
   float edge=max(.01,(1.-clamp(along,0.,1.))*.3*(1.+.12*sin(along*22.+phase*6.283)));
   alpha=(1.-smoothstep(edge*.5,edge,side))*smoothstep(-.04,.04,along)*(1.-smoothstep(.75,1.,along))*.75;
   color=mix(vec3(1.,.92,.62),color,clamp(smoothstep(0.,.8,along)+side,0.,1.));
  }else if(type<3.5){
   for(int k=0;k<8;k++){float t=fract(float(k)/8.+phase);vec2 q=origin+d*t;float s=r*(.06+t*.12);alpha+=exp(-dot(p-q,p-q)/(s*s))*(1.-t)*.12;}
  }else{
   for(int k=0;k<8;k++){float t=fract(float(k)/8.+phase);vec2 q=(p-origin-d*t)/vec2(r*(.3+t*.3),r*.08);alpha+=(1.-smoothstep(.03,.15,abs(length(q)-1.)))*(1.-t)*.45;}
  }
  alpha=clamp(alpha*uColor[i].a,0.,.85);
  result.rgb=color*alpha+result.rgb*(1.-alpha);result.a=alpha+result.a*(1.-alpha);
 }
 gl_FragColor=result;
}`;
function shader(gl,type,source) {
  const object=gl.createShader(type);gl.shaderSource(object,source);gl.compileShader(object);
  if(!gl.getShaderParameter(object,gl.COMPILE_STATUS)){gl.deleteShader(object);throw Error('shader');}
  return object;
}
export default function DiagramEffects(props) {
  const canvas=useRef(null),runtime=useRef(null),[available,setAvailable]=useState(false);
  const hasEffects=props.effects.length>0;
  useEffect(()=>{
    if(!hasEffects)return;
    const target=canvas.current;let gl,program,buffer,vertex,fragment;
    const dispose=()=>{
      if(gl){if(program)gl.deleteProgram(program);if(buffer)gl.deleteBuffer(buffer);if(vertex)gl.deleteShader(vertex);if(fragment)gl.deleteShader(fragment);}
      runtime.current=null;
    };
    const lost=event=>{event.preventDefault();runtime.current=null;setAvailable(false);};
    target.addEventListener('webglcontextlost',lost);
    try {
      gl=target.getContext('webgl',{alpha:true,premultipliedAlpha:true,antialias:false,preserveDrawingBuffer:false});
      if(!gl)throw Error('WebGL unavailable');
      vertex=shader(gl,gl.VERTEX_SHADER,VERTEX);fragment=shader(gl,gl.FRAGMENT_SHADER,FRAGMENT);
      program=gl.createProgram();gl.attachShader(program,vertex);gl.attachShader(program,fragment);gl.linkProgram(program);
      if(!gl.getProgramParameter(program,gl.LINK_STATUS))throw Error('link');
      gl.useProgram(program);buffer=gl.createBuffer();gl.bindBuffer(gl.ARRAY_BUFFER,buffer);gl.bufferData(gl.ARRAY_BUFFER,new Float32Array([-1,-1,1,-1,-1,1,1,1]),gl.STATIC_DRAW);
      const position=gl.getAttribLocation(program,'aPosition');gl.enableVertexAttribArray(position);gl.vertexAttribPointer(position,2,gl.FLOAT,false,0,0);
      const uniforms=Object.fromEntries(['uResolution','uEffect[0]','uDirection[0]','uColor[0]','uCount'].map(name=>[name,gl.getUniformLocation(program,name)]));
      runtime.current={gl,uniforms};setAvailable(true);
    }catch(_){dispose();setAvailable(false);}
    return ()=>{target.removeEventListener('webglcontextlost',lost);dispose();};
  },[hasEffects]);
  useEffect(()=>{
    const current=runtime.current;if(!current)return;
    const {gl,uniforms}=current,effects=new Float32Array(16),directions=new Float32Array(16),colors=new Float32Array(16);
    props.effects.forEach((e,i)=>{
      effects.set([...e.point,e.radius,['glow','flow','flame','steam','water'].indexOf(e.type)],i*4);
      directions.set([...e.end,e.phase,0],i*4);
      const color=props.tones[e.tone||'accent'];
      colors.set([1,3,5].map(start=>parseInt(color.slice(start,start+2),16)/255).concat(e.opacity),i*4);
    });
    // One draw per scene pose. There is no WebGL timer or animation worker.
    const target=canvas.current;target.width=1000;target.height=600;
    gl.viewport(0,0,1000,600);gl.uniform2f(uniforms.uResolution,1000,600);gl.uniform4fv(uniforms['uEffect[0]'],effects);gl.uniform4fv(uniforms['uDirection[0]'],directions);gl.uniform4fv(uniforms['uColor[0]'],colors);gl.uniform1i(uniforms.uCount,props.effects.length);gl.drawArrays(gl.TRIANGLE_STRIP,0,4);
  },[props.effects,props.tones,available]);
  if(!hasEffects)return null;
  return <>{!available&&<VectorEffects {...props}/>}<canvas ref={canvas} aria-hidden="true" data-diagram-effects={available?'webgl':'vector'} style={{position:'absolute',inset:0,width:'100%',height:'100%',pointerEvents:'none',opacity:available?1:0}}/></>;
}
